import { GlobalContext } from "components/GlobalContext";
import { createFilterableArrayColumnDefinition, createFilterableNumberRangeColumnDefinition, createFilterableSelectColumnDefinition, IColumnDefinition } from "components/SortableTable";
import IEquipment, { IEquipmentCreateOrUpdate, IEquipmentFilters, IEquipmentInitResult } from "models/sql/IEquipment";
import { useContext, useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import EquipmentService from "services/EquipmentService";
import { formatDate } from "utilities/StringUtility";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faEnvelope, faRightFromBracket, faRightToBracket } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import MiscService from "services/MiscService";
import { addNewLogEntry, applyGlobalFilterData } from "utilities/MiscUtilities";
import CommonLookupDisplay from "components/CommonLookupDisplay";
import useLocalStorage from "react-use-localstorage";
import { LinkContainer } from "react-router-bootstrap";
import { getStatus, getStatusDate } from "utilities/EquipmentUtility";
import { getDaysBetweenDates } from "utilities/DateUtilities";

interface IAirCardLookupProps {
    baseData?: IEquipmentInitResult;
}

const COUNTRIES_FILTER_KEY = "countries";
const STATES_FILTER_KEY = "states";

const AirCardsLookup = (props: IAirCardLookupProps) => {
    const [filter, setFilter] = useState<IEquipmentFilters>({});
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [mode, setMode] = useLocalStorage("aircardLookupMode", searchParams.get("status") ?? undefined);

    useEffect(
        () => {
            if (JSON.stringify(filter) !== "{}") {
                var newFilter = { ...filter, status: mode };
                setFilter(newFilter);
                setSearchParams(newFilter as any);
            }
        },
        [mode]);

    const exportTableDataAsync = async () => {
        const accessToken = await globalContext.getAccessTokenAsync();
        EquipmentService.DownloadExcelFileAsync(
            accessToken,
            "AirCardExport.xlsx",
            applyGlobalFilterData(
                STATES_FILTER_KEY,
                COUNTRIES_FILTER_KEY,
                filter,
                globalContextRef.current.region,
                globalContextRef.current.organizationType));
    };

    const saveEntryUpdate = async (equipment: IEquipment, allData: IEquipment[]) => {
        globalContext.setLoading(true);
        const accessToken = await globalContext.getAccessTokenAsync();

        const data: IEquipmentCreateOrUpdate = {
            equipmentId: equipment.equipmentId,
            dealerId: equipment.dealerId,
            fireDepartmentId: equipment.fireDepartmentId,
            stationId: equipment.stationId,
            productId: equipment.productId,
            serialNumber: equipment.serialNumber,
            parentEquipmentId: equipment.parentEquipmentId,
            startedService: equipment.startedService || undefined,
            registrationDate: equipment.registrationDate || undefined,
            extraData: equipment.extraData,
            notes: equipment.notes,
            registrationEmail: equipment.registrationEmail,
            registrationName: equipment.registrationName,
            registrationPostalCode: equipment.registrationPostalCode,
            demoRequestDate: equipment.demoRequestDate || undefined,
            demoApprovalDate: equipment.demoApprovalDate || undefined,
            demoStartDate: equipment.demoStartDate || undefined,
            demoEndDate: equipment.demoEndDate || undefined,
            demoRequestName: equipment.demoRequestName,
            demoRequestEmail: equipment.demoRequestEmail,
            systemDataJson: equipment.systemDataJson,
            location: equipment.location,
            building: equipment.building,
            region: equipment.region,
            country: equipment.country,
            city: equipment.city,
            departmentName: equipment.departmentName,
            created: equipment.created,
            outOfServiceDate: equipment.outOfServiceDate || undefined,
            repAssignDate: equipment.repAssignDate || undefined,
            salesRepId: equipment.salesRepId,
            score: equipment.score,
            shippingNotes: equipment.shippingNotes,
            stationNumber: equipment.stationNumber,
            address1: equipment.address1,
            address2: equipment.address2,
            state: equipment.state,
            postalCode: equipment.postalCode,
            contactName: equipment.contactName,
            contactTitle: equipment.contactTitle,
            contactEmail: equipment.contactEmail,
            qaCompleteDate: equipment.qaCompleteDate,
            receivedDate: equipment.receivedDate,
            shippedDate: equipment.shippedDate,
            demoDuration: equipment.demoDuration
        };

        await EquipmentService.CreateOrUpdateAsync(accessToken, data);
        allData[allData.findIndex(x => x.equipmentId === equipment.equipmentId)] = {
            ...data,
            parentEquipment: equipment.parentEquipment,
            product: equipment.product,
            dealer: equipment.dealer,
            salesRep: equipment.salesRep,
            fireDepartment: equipment.fireDepartment
        };
        globalContext.setLoading(false);
    };

    const getColumns = (): IColumnDefinition<IEquipment, IEquipmentFilters>[] => {
        return [
            {
                propName: "equipmentId",
                title: "Actions",
                customRenderer: (value, row, allData) => <td className="align-top" key={`followup${row.equipmentId}`}>
                    <>
                        {
                            (row.systemDataJson ?? "").indexOf("follow up email") > -1 ?
                                <span className="ps-2"><FontAwesomeIcon icon={faCheck as IconProp} /></span> :
                                <span
                                    title="Send Follow Up Email"
                                    className="quick-link"
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        globalContext.showModal({
                                            content:
                                                <></>,
                                            title: "Send Follow Up Email",
                                            formElements: [
                                                {
                                                    key: "title",
                                                    type: "text",
                                                    title: `Email Title`,
                                                    value: "Regarding your AirCard Result"
                                                },
                                                {
                                                    key: "body",
                                                    type: "textarea",
                                                    title: `Body`,
                                                    value: `We recently received your AirCard Result and would like to discuss.`
                                                }
                                            ],
                                            okCallback:
                                                async (data) => {
                                                    var emailAddress = row.registrationEmail;
                                                    var fromEmailAddress = globalContext.userEmail;
                                                    var title = data?.[0].value ?? "";
                                                    var body = data?.[1].value ?? "";

                                                    if (emailAddress) {
                                                        const accessToken = await globalContext.getAccessTokenAsync();

                                                        await saveEntryUpdate(
                                                            {
                                                                ...addNewLogEntry(
                                                                    (new Date()),
                                                                    `${globalContext.userName} has sent an aircard follow up email to '${row.registrationEmail}'`,
                                                                    globalContext.userEmail,
                                                                    101,
                                                                    row)
                                                            },
                                                            allData);

                                                        await MiscService.sendEmailRawAsync(
                                                            accessToken,
                                                            emailAddress,
                                                            fromEmailAddress,
                                                            title,
                                                            body);

                                                        window.alert("Your follow up email has been sent to " + emailAddress);

                                                    }
                                                },
                                            cancelCallback: () => { },
                                            customActionButton: {
                                                title: "Manually Contacted",
                                                buttonType: "success",
                                                onClick: () => {
                                                    saveEntryUpdate(
                                                        {
                                                            ...addNewLogEntry(
                                                                (new Date()),
                                                                `${globalContext.userName} manually sent a follow up email to '${row.registrationEmail}'`,
                                                                globalContext.userEmail,
                                                                101,
                                                                row)
                                                        },
                                                        allData);
                                                },
                                                closesModal: true
                                            }
                                        });
                                    }}>
                                    <FontAwesomeIcon icon={faEnvelope as IconProp} />
                                </span>
                        }
                        {
                            row.systemDataJson &&
                            JSON.parse(row?.systemDataJson || "{}")?.files &&
                            (JSON.parse(row?.systemDataJson || "{}")?.files || []).map(
                                (attachment: any, index: number) => {
                                    if (attachment.title.toLocaleLowerCase() === "aircard registration image") {
                                        return (
                                            <span className="ps-2"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    globalContext.showModal({
                                                        content:
                                                            <>
                                                                <h3>{attachment.title} for {row.serialNumber}</h3>
                                                                <img style={{ width: "100%", maxHeight: "2000px" }} src={`/api/Misc/downloadFile?fileName=${attachment.fileName}`} />
                                                            </>,
                                                        title: "Send Follow Up Email"
                                                    });
                                                }}><FontAwesomeIcon icon={faRightToBracket as IconProp} /></span>);
                                    }
                                    if (attachment.title.toLocaleLowerCase() === "aircard completion image") {
                                        return (
                                            <span className="ps-2"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    globalContext.showModal({
                                                        content:
                                                            <>
                                                                <h4>{attachment.title} for <strong>{row.serialNumber}</strong></h4>
                                                                <img style={{ width: "100%", maxHeight: "2000px" }} src={`/api/Misc/downloadFile?fileName=${attachment.fileName}`} />
                                                            </>,
                                                        title: "Send Follow Up Email"
                                                    });
                                                }}><FontAwesomeIcon icon={faRightFromBracket as IconProp} /></span>);
                                    }
                                })
                        }
                    </>
                </td>
            },
            createFilterableArrayColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                "serialNumber",
                "serialNumbers",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`serialNumber${row.equipmentId}`}>
                    {
                        (row.serialNumber || "").split(",")
                            .map(
                                (e, index, array) => <>{e}{array.length > 1 && <br></br>}</>)
                    }</td>),
            {
                // @ts-ignore
                propName: "statusDate",
                title: "Status Date",
                customRenderer: (value, data) => <td className="align-top" key={`statusDate${data.equipmentId}`}>
                    {getStatusDate(data)}
                </td>,
                sortable: true
            },
            createFilterableSelectColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                "equipmentId",
                "status",
                false,
                filter,
                ["Pending", "Complete", "In-Use", "Tagged", "Assigned", "Auto Expired"].map(e => ({ value: e, title: e })),
                undefined,
                (value, data) => <td className="align-top" key={`status${data.equipmentId}`}>
                    {getStatus(data)}
                </td>,
                "Status"),
            createFilterableNumberRangeColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                "score",
                // @ts-ignore
                "Score",
                true,
                filter,
                (value: any, row: any) => <td className="align-top" key={`score${row.equipmentId}`}>
                    {value}
                </td>,
                "Score"),

            {
                // @ts-ignore
                propName: "equipmentId",
                title: "Day(s) in-Use",
                customRenderer: (value, data) => <td className="align-top" key={`daysinuse${data.equipmentId}`}>
                    {Math.floor(getDaysBetweenDates(data.registrationDate, data.outOfServiceDate) || 0)}
                </td>,
                sortable: false
            },

            {
                // @ts-ignore
                propName: "building",
                title: "Station #",
                customRenderer: (value, data) => <td className="align-top" key={`building${data.equipmentId}`}>
                    {value}
                </td>,
                sortable: false
            },
            createFilterableArrayColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                "location",
                "locations",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`location${row.equipmentId}`}>
                    {row.location}</td>,
                "Placement Location"),
            createFilterableArrayColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                "departmentName",
                "departmentNames",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`departmentName${row.equipmentId}`}>
                    {row.fireDepartment?.fireDeptName ?? row.departmentName}</td>,
                "Department"),
            createFilterableArrayColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                // @ts-ignore
                "state",
                "states",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`state${row.equipmentId}`}>
                    {row.fireDepartment?.hqstate ?? ""}</td>,
                "State"),
            createFilterableArrayColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                "country",
                "countries",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`country${row.equipmentId}`}>
                    {row.fireDepartment?.hqCountry ?? row.country}</td>,
                "Country"),
            createFilterableArrayColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                // @ts-ignore
                "repCompanies",
                "repCompanies",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`company${row.equipmentId}`}>
                    {row.salesRep?.dealer?.dealerName ?? row.dealer?.dealerName ?? row.salesRep?.company ?? ""}</td>,
                "Rep Company")
        ];
    };

    const rowClassRenderer = (row: IEquipment) => {
        return (row.systemDataJson ?? "").indexOf("follow up email") > -1 ? "followedUp" : "notFollowedUp";
    }

    const getTools = () => [
        <Form.Select
            style={{
                "display": "inline",
                "width": "130px",
                "position": "relative",
                "left": "-8px"
            }}
            className="float-start full-sm"
            onChange={async (e) => {
                setMode(e.target.value);
            }}
            value={mode}>
            <option value={undefined}>Any</option>
            <option value={"Pending"}>Pending</option>
            <option value={"Assigned"}>Assigned</option>
            <option value={"Tagged"}>Tagged</option>
            <option value={"In-Use"}>In-Use</option>
            <option value={"Complete"}>Complete</option>
            <option value={"Auto Expired"}>Auto Expired</option>
        </Form.Select>,
        <>
            {
                globalContext.roles.some(r => r === "Web.Tools") &&
                <LinkContainer to="/Tools/EquipmentMassAdd">
                    <Button
                        style={{
                            "display": "inline",
                            "width": "130px",
                            "position": "relative",
                            "left": "-8px"
                        }}
                        variant="primary"
                        className="ms-2 float-start full-sm"
                        onClick={() => {
                        }}>Mass Add</Button>
                </LinkContainer>
            }
        </>,
        <Button
            className="me-2 full-sm"
            key="downloadExcel"
            onClick={() => { exportTableDataAsync(); }}>Export</Button>,
        <>
            {
                globalContext.roles.some(r => r === "Web.Admin") &&
                <Button
                    className="me-2 full-sm"
                    key="createnew"
                    variant="success"
                    onClick={() => { navigate(`/AirCards/Detail/`) }}>Create New</Button>
            }
        </>
    ];

    return (
        <CommonLookupDisplay<IEquipmentFilters, IEquipment, IEquipmentCreateOrUpdate>
            baseFilters={{
                sortBys: ["statusDate"],
                sortDescending: true,
                productPrefixs: ["d01", "r01"],
                status: mode
            }}
            filterHints={{
                equipmentIds: "Array",
                dealerIds: "Array",
                departmentIds: "Array",
                departmentNames: "Array",
                stationIds: "Array",
                productIds: "Array",
                parentEquipmentIds: "Array",
                serialNumbers: "Array",
                extraData: "Array",
                sortBys: "Array",
                productPrefixs: "Array",
                minScore: "Number",
                maxScore: "Number",
                sortDescending: "Boolean",
                states: "Array",
                countries: "Array",
                repCompanies: "Array",
                registrationNames: "Array",
                registrationEmails: "Array",
                organizationTypes: "Array",
                stationNumbers: "Array",
                locations: "Array"
            }}
            dataTypeName="AirCard"
            dataTypeNamePlural="AirCards"
            pathName="AirCards"
            primaryIdKey="equipmentId"
            className=""
            countriesFilterKey={COUNTRIES_FILTER_KEY}
            statesFilterKey={STATES_FILTER_KEY}
            filter={filter}
            setFilter={setFilter}
            columnDefinitions={getColumns()}
            service={EquipmentService}
            tools={getTools()}
            rowClassRenderer={rowClassRenderer}
        />
    );
}

export default AirCardsLookup;
